import React, { useState } from 'react'

import {
  Input,
  Span,
  SelectDropdown,
  SearchIcon,
  Flex,
  CTABanner,
  Button,
} from '../../lib/components'
import {
  projectSort,
  NEED_MORE_INFORMATION_QUESTION,
  QUESTION_FOR_INVESTMENT,
  REQUEST_CALL_BACK,
  REQUEST_CALLBACK_BANNER_IMAGE_SRC,
} from '../../lib/utils/message'
import { whatsAppService } from '../../services/whatsapp-support'
import { InvestmentCardV2, type InvestmentCardV2Props } from '../home-v2/investment-card-v2'
import type { ComingSoonPublicItem } from '../../services/coming-soon-service'
import { ComingSoonBand } from '../coming-soon-band/coming-soon-band'
import { OpportunitySpotlight } from '../opportunity-spotlight/opportunity-spotlight'
import { formatTitleCaseWords } from '../../lib/utils/helpers'
import '../home-v2/home-v2.scss'
import './opportunity-list-v2.scss'

const INVESTMENT_SLIDER_MIN = 0
const INVESTMENT_SLIDER_MAX = 10_000_000
const INVESTMENT_SLIDER_STEP = 1000

export interface OpportunityListV2PresenterProps {
  opportunityCards: Omit<InvestmentCardV2Props, 'onCtaClick'>[]
  searchParams: {
    keyword: string
    sortBy: string
    filterBy: string
    status: string
    minInvestment: string
    maxInvestment: string
    jurisdiction: string
  }
  opportunityLoading: boolean
  isFiltering: boolean
  isLoadingMore: boolean
  handleSearchParams: (updates: Record<string, string>) => void
  onOpportunityCtaClick: (projectSlug: string, id: number) => void
  handleLoadMore: () => void
  resetSearchParams: () => void
  assetClasses: string[]
  pagination: { total: number; limit: number; count: number; offset: number }
  hasMore: boolean
  comingSoonItems: ComingSoonPublicItem[]
  comingSoonLoading: boolean
}

const STATUS_OPEN = 'PUBLISHED'
const STATUS_PAST = 'CLOSED'

function parseAssetClassFilterParam(filterBy: string): string[] {
  if (!filterBy) return []
  return filterBy
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function OpportunityListV2Presenter({
  opportunityCards,
  onOpportunityCtaClick,
  searchParams,
  handleSearchParams,
  pagination,
  opportunityLoading,
  isFiltering,
  isLoadingMore,
  resetSearchParams,
  assetClasses,
  handleLoadMore,
  hasMore,
  comingSoonItems,
  comingSoonLoading,
}: OpportunityListV2PresenterProps) {
  const filtersRef = React.useRef<HTMLElement>(null)
  const [showFiltersMobile, setShowFiltersMobile] = useState(false)

  const toggleFiltersMobile = () => {
    setShowFiltersMobile((prev) => {
      const next = !prev
      if (next) {
        setTimeout(() => {
          filtersRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
      return next
    })
  }

  const jurisdictions = searchParams.jurisdiction
    ? searchParams.jurisdiction
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
    : []
  const selectedAssetClasses = parseAssetClassFilterParam(searchParams.filterBy)
  const [geographyInput, setGeographyInput] = useState('')

  React.useEffect(() => {
    if (!searchParams.jurisdiction) setGeographyInput('')
  }, [searchParams.jurisdiction])

  const hasActiveFilters =
    searchParams.filterBy ||
    searchParams.minInvestment ||
    searchParams.maxInvestment ||
    searchParams.jurisdiction

  const appliedFilterLabels: { key: string; label: string }[] = []
  selectedAssetClasses.forEach((ac) => {
    appliedFilterLabels.push({
      key: `filterBy:${ac}`,
      label: formatTitleCaseWords(ac),
    })
  })
  if (searchParams.minInvestment || searchParams.maxInvestment) {
    const range =
      searchParams.minInvestment && searchParams.maxInvestment
        ? `$${searchParams.minInvestment} - $${searchParams.maxInvestment}`
        : searchParams.minInvestment
        ? `Min $${searchParams.minInvestment}`
        : `Max $${searchParams.maxInvestment}`
    appliedFilterLabels.push({ key: 'range', label: range })
  }
  jurisdictions.forEach((j) => {
    appliedFilterLabels.push({ key: `jurisdiction:${j}`, label: j })
  })
  if (searchParams.status === STATUS_OPEN) {
    appliedFilterLabels.push({ key: 'status', label: 'Open for Investment' })
  } else if (searchParams.status === STATUS_PAST) {
    appliedFilterLabels.push({ key: 'status', label: 'Closed' })
  }

  const removeAppliedFilter = (key: string) => {
    if (key.startsWith('filterBy:')) {
      const toRemove = key.replace(/^filterBy:/, '')
      const next = selectedAssetClasses.filter((x) => x !== toRemove)
      handleSearchParams({ filterBy: next.join(',') })
    } else if (key === 'range') {
      handleSearchParams({ minInvestment: '', maxInvestment: '' })
    } else if (key.startsWith('jurisdiction:')) {
      const toRemove = key.replace(/^jurisdiction:/, '')
      const next = jurisdictions.filter((j) => j !== toRemove)
      handleSearchParams({ jurisdiction: next.join(',') })
      setGeographyInput('')
    } else if (key === 'status') handleSearchParams({ status: '' })
  }

  const assetClassTabs = ['All', ...assetClasses]

  const showComingSoonBand = comingSoonLoading || comingSoonItems.length > 0
  const highlightedCard = opportunityCards.find((card) => card.isHighlighted)

  return (
    <div className='opportunities-v2'>
      {highlightedCard ? (
        <OpportunitySpotlight
          title={highlightedCard.title}
          description={highlightedCard.description}
          category={highlightedCard.category}
          minInvestment={highlightedCard.minInvestment}
          onInvestClick={() =>
            onOpportunityCtaClick(highlightedCard.projectSlug, highlightedCard.id)
          }
          testIdPrefix='opportunities-v2'
        />
      ) : showComingSoonBand ? (
        <ComingSoonBand
          items={comingSoonItems}
          loading={comingSoonLoading}
          testIdPrefix='opportunities-v2'
        />
      ) : (
        <section className='opportunities-v2__hero' aria-label='Investment marketplace'>
          <div className='opportunities-v2__hero-inner'>
            <h2 className='opportunities-v2__hero-title'>
              <span className='opportunities-v2__hero-title-line'>Diversify beyond</span>
              <span className='opportunities-v2__hero-title-line'>Stocks & Bonds</span>
            </h2>
          </div>
        </section>
      )}

      <div className='opportunities-v2__content-bg'>
        <div className='opportunities-v2__content-inner'>
          <div className='opportunities-v2__asset-tabs'>
            {assetClassTabs.map((label) => {
              const value = label === 'All' ? '' : label
              const isActive =
                (value === '' && selectedAssetClasses.length === 0) ||
                (value !== '' &&
                  selectedAssetClasses.length === 1 &&
                  selectedAssetClasses[0] === value)
              return (
                <button
                  key={label}
                  type='button'
                  className={`opportunities-v2__asset-tab ${
                    isActive ? 'opportunities-v2__asset-tab--active' : ''
                  }`}
                  onClick={() => handleSearchParams({ filterBy: value })}
                >
                  {label === 'All' ? 'All' : formatTitleCaseWords(label)}
                </button>
              )
            })}
          </div>

          <div className='opportunities-v2__toolbar'>
            <div className='opportunities-v2__toolbar-mobile-row'>
              <button
                type='button'
                className='opportunities-v2__filters-trigger'
                onClick={toggleFiltersMobile}
                aria-label={showFiltersMobile ? 'Close filters' : 'Open filters'}
                aria-expanded={showFiltersMobile}
              >
                Filters
                {hasActiveFilters && appliedFilterLabels.length > 0 && (
                  <span className='opportunities-v2__filters-badge'>
                    {appliedFilterLabels.length}
                  </span>
                )}
              </button>
              <div className='opportunities-v2__sort opportunities-v2__sort--mobile'>
                <label htmlFor='sort-v2-mobile' className='opportunities-v2__sort-label'>
                  Sort
                </label>
                <SelectDropdown
                  name='sortBy'
                  id='sort-v2-mobile'
                  formId=''
                  list={projectSort}
                  placeholder='Recommended'
                  value={searchParams.sortBy}
                  handleChange={(val) => handleSearchParams({ sortBy: val })}
                  classes={['opportunities-v2__sort-select']}
                  dataTestId='opportunity-v2-sort-dropdown-mobile'
                />
              </div>
            </div>
            <div className='opportunities-v2__search'>
              <span className='opportunities-v2__search-icon'>
                <SearchIcon />
              </span>
              <Input
                name='keyword'
                value={searchParams.keyword}
                handleChange={(e) => handleSearchParams({ keyword: e.target.value })}
                placeholder='Search by name, asset class, or location'
              />
            </div>
            <span className='opportunities-v2__toolbar-divider' aria-hidden />
            <div className='opportunities-v2__sort opportunities-v2__sort--desktop'>
              <label htmlFor='sort-v2' className='opportunities-v2__sort-label'>
                Sort
              </label>
              <SelectDropdown
                name='sortBy'
                id='sort-v2'
                formId=''
                list={projectSort}
                placeholder='Recommended'
                value={searchParams.sortBy}
                handleChange={(val) => handleSearchParams({ sortBy: val })}
                classes={['opportunities-v2__sort-select']}
                dataTestId='opportunity-v2-sort-dropdown'
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className='opportunities-v2__applied'>
              <span className='opportunities-v2__applied-label'>Applied:</span>
              <div className='opportunities-v2__applied-chips'>
                {appliedFilterLabels.map(({ key, label }) => (
                  <span key={`${key}-${label}`} className='opportunities-v2__chip'>
                    {label}
                    <button
                      type='button'
                      aria-label={`Remove ${label}`}
                      className='opportunities-v2__chip-remove'
                      onClick={() => removeAppliedFilter(key)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <button
                type='button'
                className='opportunities-v2__clear-all'
                onClick={resetSearchParams}
              >
                Clear all
              </button>
            </div>
          )}

          <div className='opportunities-v2__main'>
            <aside
              id='opportunities-v2-filters'
              className={`opportunities-v2__filters ${
                showFiltersMobile ? 'opportunities-v2__filters--open-mobile' : ''
              }`}
              ref={filtersRef}
            >
              <h3 className='opportunities-v2__filters-title'>ADVANCED FILTERS</h3>

              <div className='opportunities-v2__filter-group'>
                <h4 className='opportunities-v2__filter-label'>Asset Class</h4>
                <div className='opportunities-v2__checkboxes'>
                  {assetClasses.map((ac) => (
                    <label key={ac} className='opportunities-v2__checkbox-label'>
                      <input
                        type='checkbox'
                        checked={selectedAssetClasses.includes(ac)}
                        onChange={() => {
                          const next = selectedAssetClasses.includes(ac)
                            ? selectedAssetClasses.filter((x) => x !== ac)
                            : [...selectedAssetClasses, ac]
                          handleSearchParams({ filterBy: next.join(',') })
                        }}
                      />
                      <span>{formatTitleCaseWords(ac)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className='opportunities-v2__filter-group'>
                <h4 className='opportunities-v2__filter-label'>Investment Range</h4>
                <div className='opportunities-v2__range-inputs'>
                  <div>
                    <label htmlFor='min-inv'>Min $</label>
                    <input
                      id='min-inv'
                      type='number'
                      min={0}
                      step={1000}
                      value={searchParams.minInvestment}
                      onChange={(e) => handleSearchParams({ minInvestment: e.target.value })}
                      placeholder='e.g. 1000'
                    />
                  </div>
                  <div>
                    <label htmlFor='max-inv'>Max $</label>
                    <input
                      id='max-inv'
                      type='number'
                      min={0}
                      step={1000}
                      value={searchParams.maxInvestment}
                      onChange={(e) => handleSearchParams({ maxInvestment: e.target.value })}
                      placeholder='e.g. 100000'
                    />
                  </div>
                </div>
                <div className='opportunities-v2__range-sliders'>
                  <label htmlFor='min-slider' className='opportunities-v2__slider-label'>
                    Min: $
                    {(Number(searchParams.minInvestment) || 0).toLocaleString('en-US', {
                      maximumFractionDigits: 0,
                    })}
                  </label>
                  <input
                    id='min-slider'
                    type='range'
                    min={INVESTMENT_SLIDER_MIN}
                    max={INVESTMENT_SLIDER_MAX}
                    step={INVESTMENT_SLIDER_STEP}
                    value={
                      searchParams.minInvestment
                        ? Number(searchParams.minInvestment)
                        : INVESTMENT_SLIDER_MIN
                    }
                    onChange={(e) =>
                      handleSearchParams({
                        minInvestment: e.target.value,
                      })
                    }
                    className='opportunities-v2__range-slider'
                  />
                  <label htmlFor='max-slider' className='opportunities-v2__slider-label'>
                    Max: $
                    {(Number(searchParams.maxInvestment) || INVESTMENT_SLIDER_MAX).toLocaleString(
                      'en-US',
                      { maximumFractionDigits: 0 },
                    )}
                  </label>
                  <input
                    id='max-slider'
                    type='range'
                    min={INVESTMENT_SLIDER_MIN}
                    max={INVESTMENT_SLIDER_MAX}
                    step={INVESTMENT_SLIDER_STEP}
                    value={
                      searchParams.maxInvestment
                        ? Number(searchParams.maxInvestment)
                        : INVESTMENT_SLIDER_MAX
                    }
                    onChange={(e) =>
                      handleSearchParams({
                        maxInvestment: e.target.value,
                      })
                    }
                    className='opportunities-v2__range-slider'
                  />
                </div>
              </div>

              <div className='opportunities-v2__filter-group'>
                <h4 className='opportunities-v2__filter-label'>GEOGRAPHY</h4>
                {jurisdictions.length > 0 ? (
                  <div className='opportunities-v2__geography-pills'>
                    {jurisdictions.map((j) => (
                      <span key={j} className='opportunities-v2__geography-pill'>
                        {j}
                        <button
                          type='button'
                          aria-label={`Remove ${j}`}
                          className='opportunities-v2__chip-remove'
                          onClick={() => {
                            const next = jurisdictions.filter((x) => x !== j)
                            handleSearchParams({ jurisdiction: next.join(',') })
                            setGeographyInput('')
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className='opportunities-v2__search-input-wrap'>
                  <SearchIcon />
                  <input
                    type='text'
                    placeholder='Search region...'
                    value={geographyInput}
                    onChange={(e) => setGeographyInput(e.target.value)}
                    onBlur={() => {
                      const value = geographyInput.trim()
                      if (value && !jurisdictions.includes(value)) {
                        handleSearchParams({
                          jurisdiction: [...jurisdictions, value].join(','),
                        })
                        setGeographyInput('')
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const value = geographyInput.trim()
                        if (value && !jurisdictions.includes(value)) {
                          handleSearchParams({
                            jurisdiction: [...jurisdictions, value].join(','),
                          })
                          setGeographyInput('')
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div className='opportunities-v2__filter-group'>
                <h4 className='opportunities-v2__filter-label'>STATUS</h4>
                <div className='opportunities-v2__checkboxes'>
                  <label className='opportunities-v2__checkbox-label'>
                    <input
                      type='radio'
                      name='opportunity-status-v2'
                      checked={searchParams.status === STATUS_OPEN}
                      onChange={() => handleSearchParams({ status: STATUS_OPEN })}
                    />
                    <span>Open for Investment</span>
                  </label>
                  <label className='opportunities-v2__checkbox-label'>
                    <input
                      type='radio'
                      name='opportunity-status-v2'
                      checked={searchParams.status === STATUS_PAST}
                      onChange={() => handleSearchParams({ status: STATUS_PAST })}
                    />
                    <span>Closed</span>
                  </label>
                </div>
              </div>
            </aside>

            <div className='opportunities-v2__content'>
              <p className='opportunities-v2__results-info'>
                {pagination.total === 0
                  ? 'No results'
                  : `Showing 1-${opportunityCards.length} of ${pagination.total} results`}
              </p>
              <div
                className={`opportunities-v2__cards ${
                  isFiltering ? 'opportunities-v2__cards--loading' : ''
                }`}
                data-testid='opportunity-list-v2-container'
              >
                {opportunityLoading ? (
                  <>
                    {[1, 2, 3].map((idx) => (
                      <div key={idx} className='opportunities-v2__card-skeleton' />
                    ))}
                  </>
                ) : opportunityCards.length > 0 ? (
                  opportunityCards.map((card) => (
                    <InvestmentCardV2
                      key={card.id}
                      {...card}
                      onCtaClick={() => onOpportunityCtaClick(card.projectSlug, card.id)}
                    />
                  ))
                ) : (
                  <Span variant='thin' classes={['left']} text='No results found' />
                )}
                {isFiltering && (
                  <div className='opportunities-v2__cards-loader' aria-live='polite'>
                    <span className='opportunities-v2__spinner' />
                  </div>
                )}
              </div>
              {!opportunityLoading && opportunityCards.length > 0 && (hasMore || isLoadingMore) && (
                <div className='opportunities-v2__load-more-wrap'>
                  <button
                    type='button'
                    className={`opportunities-v2__load-more-btn ${
                      isLoadingMore ? 'is-loading' : ''
                    }`}
                    onClick={handleLoadMore}
                    data-testid='opportunities-v2-load-more'
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <Flex variant='row-center' classes={['opportunities-v2__cta-wrap']}>
            <CTABanner
              classes={['opportunities-v2__cta-banner']}
              heading={NEED_MORE_INFORMATION_QUESTION}
              subHeading={QUESTION_FOR_INVESTMENT}
              leadImageSrc={REQUEST_CALLBACK_BANNER_IMAGE_SRC}
              ButtonComponent={
                <div className='opportunities-v2__cta-buttons'>
                  <Button
                    classes={['auto-btn-width']}
                    variant='primary-btn'
                    text={REQUEST_CALL_BACK}
                    handleClick={() => whatsAppService.requestOpportunitiesPageSupport()}
                    testId='opportunities-v2-request-callback'
                  />
                </div>
              }
            />
          </Flex>
        </div>
      </div>
    </div>
  )
}

export { OpportunityListV2Presenter }
